import { SegmentedControl } from "@mantine/core";
import { AudioWaveform, Music, Speech } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { Catalog, CatalogAudioModel, GenerationType, Job } from "../../../api/generated/model";
import { Pill } from "../../../ui/Pill";
import { Soon } from "../../../ui/Soon";
import { MusicForm } from "./MusicForm";
import { SoundEffectsForm, type SoundFormProps } from "./SoundEffectsForm";
import { SpeechForm } from "./SpeechForm";
import classes from "./composer.module.css";

type SoundType = Extract<GenerationType, "audio.sfx" | "audio.tts" | "audio.music">;

/** Live's three modes, in its order, words and icons (`SOUND_ACTION_CONFIG`). */
const MODES: {
  type: SoundType;
  label: string;
  icon: ReactNode;
  form: (props: SoundFormProps) => ReactNode;
}[] = [
  {
    type: "audio.sfx",
    label: "Sound effects",
    icon: <AudioWaveform size={16} />,
    form: SoundEffectsForm,
  },
  { type: "audio.tts", label: "Text-to-speech", icon: <Speech size={16} />, form: SpeechForm },
  { type: "audio.music", label: "Music", icon: <Music size={16} />, form: MusicForm },
];

/** The Pro Studio redesign's words and order for the same three (spec ui-perf/pro-studio-redesign). */
const STYLES: { type: SoundType; label: string }[] = [
  { type: "audio.tts", label: "Voiceover" },
  { type: "audio.sfx", label: "Sound effect" },
  { type: "audio.music", label: "Music" },
];

/** The catalog's models for a mode, in its order. */
function modelsOf(catalog: Catalog, type: SoundType): CatalogAudioModel[] {
  return catalog.audio_models.filter((model) => model.job_types.includes(type));
}

/** Whether the catalog has a model for any of the composer's modes: else there is no composer. */
export function soundsOffered(catalog: Catalog): boolean {
  return MODES.some((mode) => modelsOf(catalog, mode.type).length > 0);
}

export interface SoundsComposerProps {
  catalog: Catalog;
  onStarted: (job: Job) => void;
  /** In a card of its own, not floating (`GlassPanel`'s `inline`, the Pro Studio redesign). */
  inline?: boolean | undefined;
  /** The redesign's Voiceover, Sound effect and Music in place of live's pills, Voiceover first;
   *  a kind the catalog has no model for is disabled. */
  styles?: boolean | undefined;
}

/**
 * Live's Sounds composer (#235, `design-ref/sound/`): the mode pills above a floating panel,
 * Sound effects first, and the chosen mode's form. A mode the catalog has no model for keeps its
 * pill, Coming soon; with none at all there is no composer.
 */
export function SoundsComposer({ catalog, onStarted, inline, styles }: SoundsComposerProps) {
  const offered = MODES.filter((mode) => modelsOf(catalog, mode.type).length > 0);
  const first = styles === true ? STYLES.find((each) => modelsOf(catalog, each.type).length > 0) : offered[0];
  const [type, setType] = useState<SoundType | null>(first?.type ?? null);
  const mode = offered.find((each) => each.type === type) ?? offered[0];
  if (mode === undefined) return null;
  const above = styles === true ? (
    // A box of its own: the inline panel strips its toolbar's direct child of its look.
    <div>
      <SegmentedControl
        aria-label="Kind of sound"
        radius="xl"
        size="xs"
        value={mode.type}
        onChange={(value) => setType(value as SoundType)}
        data={STYLES.map((each) => ({
          value: each.type,
          label: each.label,
          disabled: modelsOf(catalog, each.type).length === 0,
        }))}
      />
    </div>
  ) : (
    <div className={classes.modes}>
      {MODES.map((each) =>
        modelsOf(catalog, each.type).length === 0 ? (
          <Soon key={each.type}>
            {(soon) => <Pill {...soon} label={each.label} icon={each.icon} />}
          </Soon>
        ) : (
          <Pill
            key={each.type}
            label={each.label}
            icon={each.icon}
            active={each.type === mode.type}
            aria-pressed={each.type === mode.type}
            onClick={() => setType(each.type)}
          />
        ),
      )}
    </div>
  );
  const Form = mode.form;
  // Each mode has its own form, as live's do; switching to a mode opens it at its model's defaults.
  return (
    <Form
      key={mode.type}
      models={modelsOf(catalog, mode.type)}
      voiceDesign={catalog.audio_models.find((model) => model.job_types.includes("voice.design"))}
      above={above}
      onStarted={onStarted}
      inline={inline}
    />
  );
}
